import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  fetchOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  setSelectedOrganization,
  clearSelectedOrganization,
} from '../store/slices/organizationsSlice';
import OrganizationList from '../components/organizations/OrganizationList';
import OrganizationForm from '../components/organizations/OrganizationForm';
import OrganizationFilters from '../components/organizations/OrganizationFilters';
import BulkOrganizationUpload from '../components/organizations/BulkOrganizationUpload';
import ScrollHeader from '../components/common/ScrollHeader';
import TableSkeleton from '../components/common/TableSkeleton';
import FiltersSkeleton from '../components/common/FiltersSkeleton';
import { Organization, OrganizationCreate, OrganizationUpdate } from '../types';
import { PlusIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';

const Organizations = () => {
  const dispatch = useAppDispatch();
  const { organizations, selectedOrganization, isLoading, error } = useAppSelector((state) => state.organizations);
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [showForm, setShowForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    sort: 'org_id',
    order: 'asc'
  });

  const loadOrganizations = (page = 1) => {
    dispatch(fetchOrganizations({
      skip: (page - 1) * pageSize,
      limit: pageSize,
      ...filters,
    }));
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    loadOrganizations(1); // Reset to first page when changing page size
  };

  useEffect(() => {
    loadOrganizations();
  }, [filters, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshData = async () => {
    // Refresh organizations list
    loadOrganizations();
  };

  const handleSubmit = async (formData: OrganizationCreate | OrganizationUpdate) => {
    try {
      if (selectedOrganization) {
        await dispatch(updateOrganization({ id: selectedOrganization.org_id, data: formData as OrganizationUpdate })).unwrap();
      } else {
        await dispatch(createOrganization(formData as OrganizationCreate)).unwrap();
      }
      setShowForm(false);
      dispatch(clearSelectedOrganization());
      await refreshData();
    } catch (err) {
      console.error('Failed to save organization:', err);
    }
  };

  const handleEdit = (organization: Organization) => {
    dispatch(setSelectedOrganization(organization));
    setShowForm(true);
  };

  const handleDelete = async (orgId: number) => {
    try {
      await dispatch(deleteOrganization(orgId)).unwrap();
      await refreshData();
    } catch (err) {
      console.error('Failed to delete organization:', err);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  if (isLoading && !organizations) {
    return (
      <div className="space-y-8">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="h-7 w-1/4 bg-light-hover dark:bg-dark-hover rounded-lg animate-pulse" />
          <div className="w-40 h-10 bg-light-hover dark:bg-dark-hover rounded-lg animate-pulse" />
        </div>

        {/* Filters Skeleton */}
        <FiltersSkeleton />

        {/* Table Skeleton */}
        <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border overflow-hidden">
          <TableSkeleton rows={10} columns={6} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <ScrollHeader title="Organizations" />
        {currentUser?.role === 'super_admin' && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowBulkUpload(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-700 dark:text-gray-200 hover:bg-light-hover dark:hover:bg-dark-hover transition-colors duration-200"
            >
              <ArrowUpTrayIcon className="w-5 h-5" />
              Bulk Upload
            </button>
            <button
              onClick={() => {
                dispatch(clearSelectedOrganization());
                setShowForm(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-200"
            >
              <PlusIcon className="w-5 h-5" />
              Add Organization
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500">
          {error}
        </div>
      )}

      {showForm ? (
        <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border p-6">
          <OrganizationForm
            organization={selectedOrganization || undefined}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              dispatch(clearSelectedOrganization());
            }}
          />
        </div>
      ) : (
        <>
          <OrganizationFilters
            onSearchChange={(search) => handleFilterChange('search', search)}
            onStatusChange={(status) => handleFilterChange('status', status)}
            currentFilters={filters}
          />
          <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border overflow-hidden">
            {isLoading ? (
              <TableSkeleton rows={10} columns={6} />
            ) : organizations && organizations.items.length > 0 ? (
              <OrganizationList
                organizations={organizations}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onPageChange={(page) => loadOrganizations(page)}
                onPageSizeChange={handlePageSizeChange}
                currentPage={organizations.page || 1}
                pageSize={pageSize}
                currentUserRole={currentUser?.role}
              />
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No organizations found
              </div>
            )}
          </div>
        </>
      )}

      {showBulkUpload && (
        <BulkOrganizationUpload
          onClose={() => setShowBulkUpload(false)}
          onSuccess={async () => {
            setShowBulkUpload(false);
            await refreshData();
          }}
        />
      )}
    </div>
  );
};

export default Organizations;
